/**
 * temporal_resolve — will the post pipeline build a TEMPORAL resolve (TAAU or TRAA) for this preset on this backend?
 * (gaps wave round 2, 2026-09-28). Pure.
 *
 * Two decisions hang on the answer and both read the PRESET, which is not the pipeline: the clouds' `afterAA`
 * composite (PostModule — a composite placed after the AA stage protects the clouds from a temporal resolve's smear;
 * with no temporal pass it only lands the clouds' LINEAR radiance on display-referred colour, because the AA-first
 * FSR path and FXAA tone-map first), and the far trees' dithered frame pick (EnvironmentModule → impostor.ts — the
 * dither is integrated by a temporal resolve; with none it shimmers). Both used `preset.upscaleMode === 'taau'`, but
 * TAAU is WebGPU-only (the engine's `_taauSupported`: the velocity MRT drops non-MRT draws on WebGL2) — so on WebGL2
 * high / ultra the clouds sat after the tone map and the trees dithered with nothing to resolve them.
 *
 * The engine carries the same rule for the clouds (`cloudsCompositeAfterAA` in PostProcessingPipeline: honoured only
 * when a velocity target exists and TAAU or TRAA is built); this is the preset-side half, and the impostors' only one.
 */

export interface TemporalInputs {
  /** The preset's FSR ratio key ('performance' … 'ultra'), or 'off' / undefined for no upscale. */
  upscale?: string | null;
  /** 'fsr' | 'taau' — the preset's upscaler. */
  upscaleMode?: string | null;
  /** The preset's TRAA pass is on (`post.traa.enabled` — the AA menu's 'traa'). */
  traa?: boolean;
  /** The renderer runs the WebGPU backend (false on the WebGL2 fallback). */
  webgpu: boolean;
  /** An immersive XR session is presenting (the upscale is skipped then — the session's framebuffer scale governs). */
  xrPresenting?: boolean;
}

/** TAAU is built: an upscale is on, its mode is 'taau', the backend is WebGPU, and no XR session presents. */
export function taauPlanned(i: TemporalInputs): boolean {
  return !!i.upscale && i.upscale !== 'off' && i.upscaleMode === 'taau' && i.webgpu && !i.xrPresenting;
}

/** Any temporal resolve is built: TAAU (above) or the TRAA pass. */
export function temporalResolvePlanned(i: TemporalInputs): boolean {
  return taauPlanned(i) || !!i.traa;
}

/** The clouds' `afterAA` request: only when a temporal resolve will run (else they belong in the linear chain). */
export function cloudsAfterAAFor(i: TemporalInputs): boolean {
  return temporalResolvePlanned(i);
}

/** The impostors' dithered frame pick: the blend is on AND a temporal resolve integrates the dither. */
export function impostorDitherFor(impostorBlend: boolean, i: TemporalInputs): boolean {
  return impostorBlend && temporalResolvePlanned(i);
}

/** The renderer's backend, read by the property three sets (`isWebGPUBackend`), never by the class name — a production
 *  build minifies `WebGPUBackend` (the shipped bundle calls it `RF`), so a name test reads WebGL2 on every WebGPU page. */
export function isWebGPURenderer(native: unknown): boolean {
  const b = (native as { backend?: { isWebGPUBackend?: boolean; constructor?: { name?: string } } } | null | undefined)?.backend;
  return b?.isWebGPUBackend === true || b?.constructor?.name === 'WebGPUBackend';
}
